// ────────────────────────────────────────────────────────────────────────────
// Per-band options panel.
//
// One sub-inspector per band kind, dispatched off the discriminant. Each one
// edits its band immutably and hands the whole replacement band back up — the
// builder holds a single draft layout, so there is no partial-update path and
// no way for two options to disagree about what the band currently is.
// ────────────────────────────────────────────────────────────────────────────
import { cn } from '@my/ui';
import { Plus } from 'lucide-react';
import {
  AlignPicker,
  Checkbox,
  FieldPicker,
  InspectorGroup,
  InspectorRow,
  NumberField,
  SelectField,
  TextField,
} from './controls';
import { LinesBandInspector } from './LinesBandInspector';
import { OrganisationBandInspector } from './OrganisationBandInspector';
import { withLabelOverride, type LabelOverrides } from '../lib/labels';
import {
  DEFAULT_ADDRESS_FORMAT,
  DEFAULT_ADDRESS_WRAP_CHARS,
  formatAddressBlock,
} from '../lib/addressFormat';
import type { DocumentOrganisation } from '../lib/documentData';
import type { BankPreviewStatus, LogoStatus } from '../lib/useOrgPreviewData';
import { TOKEN_CATALOGUE } from '../lib/tokens';
import {
  ADDRESS_FORMATS,
  ADDRESS_FORMAT_LABEL,
  BAND_COLUMNS,
  BANK_FIELDS,
  BANK_FIELD_LABEL,
  META_FIELDS,
  META_FIELD_LABEL,
  PARTY_FIELDS,
  PARTY_FIELD_LABEL,
  TOTAL_ROWS,
  TOTAL_ROW_LABEL,
  type AddressFormat,
  type Band,
  type BandColumn,
  type BankBand,
  type CustomerBand,
  type DocDetailsBand,
  type FooterBand,
  type LogoBand,
  type PageSettings,
  type TextBand,
  type TitleBand,
  type TotalsBand,
} from '../lib/types';

/**
 * A representative stored address for the layout preview in the inspector.
 * Deliberately long-ish: a four-part address looks fine under any setting, and
 * only a real Dublin address with unit, park, road, district, city, Eircode and
 * country shows why "a line per comma" is rarely what you want.
 */
const ADDRESS_PREVIEW_SOURCE =
  'Unit B8/B9, Ballymount Corporate Park, Ballymount Avenue, Dublin 12, D12 ER82, Ireland';

/**
 * Editable labels for a band's fixed rows. Values are never editable here —
 * they come from the document — so this is labels only, and blanking a box
 * restores the standard wording.
 */
function RowLabelEditor<K extends string>({
  keys,
  labels,
  standard,
  onChange,
  note,
}: {
  keys: readonly K[];
  labels: LabelOverrides<K> | undefined;
  standard: Record<K, string>;
  onChange: (next: LabelOverrides<K>) => void;
  note: string;
}) {
  if (!keys.length) return null;
  return (
    <div className="mt-1.5 border-t border-slate-100 pt-1.5">
      <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
        Row labels
      </p>
      {keys.map((key) => (
        <InspectorRow key={key} label={standard[key]}>
          <TextField
            className="w-36"
            value={labels?.[key] ?? ''}
            placeholder={standard[key]}
            onChange={(label) => onChange(withLabelOverride(labels, key, label))}
          />
        </InspectorRow>
      ))}
      <p className="mt-1 text-[10px] leading-snug text-slate-400">{note}</p>
    </div>
  );
}

/** Collapsible token cheat-sheet; clicking a token appends it to the field. */
function TokenPalette({ onInsert }: { onInsert: (token: string) => void }) {
  const groups = [...new Set(TOKEN_CATALOGUE.map((t) => t.group))];
  return (
    <details className="mt-1 rounded border border-slate-200 bg-slate-50 px-2 py-1">
      <summary className="cursor-pointer text-[10px] font-medium uppercase tracking-wide text-slate-500">
        Insert a field
      </summary>
      <div className="mt-1 max-h-48 space-y-1.5 overflow-auto">
        {groups.map((group) => (
          <div key={group}>
            <p className="text-[10px] font-semibold text-slate-400">{group}</p>
            <div className="flex flex-wrap gap-1">
              {TOKEN_CATALOGUE.filter((t) => t.group === group).map((t) => (
                <button
                  key={t.token}
                  type="button"
                  title={t.label}
                  onClick={() => onInsert(`{{${t.token}}}`)}
                  className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] text-sky-700 ring-1 ring-slate-200 hover:ring-sky-300"
                >
                  {t.token}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}

function BodyEditor({
  value,
  onChange,
  rows = 3,
}: {
  value: string;
  onChange: (next: string) => void;
  rows?: number;
}) {
  return (
    <div>
      <textarea
        value={value}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-slate-200 p-2 text-xs text-slate-800 focus:border-sky-400 focus:outline-none"
      />
      <TokenPalette onInsert={(token) => onChange(value ? `${value} ${token}` : token)} />
    </div>
  );
}

/**
 * Says why the logo is not on the page. Without this, an org with no logo (or
 * one we could not load) sees "Show logo" ticked and simply nothing happen.
 */
function LogoStatusNote({ status, error }: { status: LogoStatus; error: string | null }) {
  if (status === 'ready') return null;
  const tone = status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-slate-50 text-slate-500';
  const message =
    status === 'none'
      ? 'This organisation has no logo yet — upload one in Settings → Organisation.'
      : status === 'loading'
        ? 'Loading your logo…'
        : 'The logo could not be loaded, so the preview leaves it out.';
  return (
    <div className={cn('mb-1 rounded px-1.5 py-1 text-[10px] leading-snug', tone)}>
      <p>{message}</p>
      {/* The server's own words. A 404 here usually means the API has not been
          restarted since the logo route was added. */}
      {error && <p className="mt-0.5 font-mono opacity-80">{error}</p>}
    </div>
  );
}

/**
 * Explains which account the payment block will print, and why it might print
 * nothing. Selection is strictly by the document's currency — see
 * `utils/bankAccounts.ts` — so "no account in EUR" is a real and silent
 * outcome unless it is said out loud.
 */
function BankStatusNote({ bank }: { bank: BankPreviewStatus }) {
  if (bank.status === 'none') {
    return (
      <div className="mb-1 rounded bg-amber-50 px-1.5 py-1 text-[10px] leading-snug text-amber-800">
        No bank account in {bank.currencyIsoCode}, so this band prints nothing. Add one in
        Settings → Organisation, or untick “Print this band” above to leave it out on purpose.
      </div>
    );
  }
  if (bank.status === 'ambiguous') {
    return (
      <div className="mb-1 rounded bg-amber-50 px-1.5 py-1 text-[10px] leading-snug text-amber-800">
        {bank.candidates} accounts in {bank.currencyIsoCode} and none marked as the default for
        that currency — “{bank.accountName}” will print. Mark one as default in
        Settings → Organisation to choose deliberately.
      </div>
    );
  }
  return (
    <p className="mb-1 rounded bg-slate-50 px-1.5 py-1 text-[10px] leading-snug text-slate-500">
      Printing “{bank.accountName}” — the {bank.currencyIsoCode} account. Documents in another
      currency print that currency's account instead.
    </p>
  );
}

function LogoInspector({
  band,
  logoStatus,
  logoError,
  onChange,
}: {
  band: LogoBand;
  logoStatus: LogoStatus;
  logoError: string | null;
  onChange: (next: LogoBand) => void;
}) {
  return (
    <InspectorGroup title="Logo">
      {band.enabled && <LogoStatusNote status={logoStatus} error={logoError} />}
      <InspectorRow label="Align">
        <AlignPicker value={band.align} onChange={(align) => onChange({ ...band, align })} />
      </InspectorRow>
      <InspectorRow label="Width">
        <NumberField
          value={band.widthPt}
          min={40}
          max={300}
          suffix="pt"
          onChange={(widthPt) => onChange({ ...band, widthPt })}
        />
      </InspectorRow>
      <p className="mt-1 text-[10px] leading-snug text-slate-400">
        Uses the logo from Settings → Organisation.
      </p>
    </InspectorGroup>
  );
}

function TitleInspector({
  band,
  onChange,
}: {
  band: TitleBand;
  onChange: (next: TitleBand) => void;
}) {
  return (
    <>
      <InspectorGroup title="Title">
        <div className="pb-1">
          <BodyEditor rows={2} value={band.text} onChange={(text) => onChange({ ...band, text })} />
        </div>
        <InspectorRow label="Align">
          <AlignPicker value={band.align} onChange={(align) => onChange({ ...band, align })} />
        </InspectorRow>
        <InspectorRow label="Size">
          <NumberField
            value={band.sizePt}
            min={8}
            max={40}
            suffix="pt"
            onChange={(sizePt) => onChange({ ...band, sizePt })}
          />
        </InspectorRow>
        <InspectorRow label="Uppercase">
          <Checkbox
            checked={band.uppercase}
            label=""
            onChange={(uppercase) => onChange({ ...band, uppercase })}
          />
        </InspectorRow>
      </InspectorGroup>

      <InspectorGroup title="Number">
        <InspectorRow label="Show document number">
          <Checkbox
            checked={band.showNumber}
            label=""
            onChange={(showNumber) => onChange({ ...band, showNumber })}
          />
        </InspectorRow>
        <InspectorRow label="Prefix" hint='e.g. "No."'>
          <TextField
            className="w-24"
            value={band.numberLabel}
            onChange={(numberLabel) => onChange({ ...band, numberLabel })}
          />
        </InspectorRow>
        <InspectorRow label="Rule underneath">
          <Checkbox
            checked={band.showRule}
            label=""
            onChange={(showRule) => onChange({ ...band, showRule })}
          />
        </InspectorRow>
      </InspectorGroup>
    </>
  );
}

function CustomerInspector({
  band,
  onChange,
}: {
  band: CustomerBand;
  onChange: (next: CustomerBand) => void;
}) {
  const format = band.addressFormat ?? DEFAULT_ADDRESS_FORMAT;
  return (
    <>
      <InspectorGroup title="Block">
        <InspectorRow label="Heading">
          <TextField
            className="w-28"
            value={band.heading}
            placeholder="Leave blank for none"
            onChange={(heading) => onChange({ ...band, heading })}
          />
        </InspectorRow>
        <FieldPicker
          all={PARTY_FIELDS}
          selected={band.fields}
          labels={PARTY_FIELD_LABEL}
          onChange={(fields) => onChange({ ...band, fields })}
        />
      </InspectorGroup>

      {band.fields.includes('addressLines') && (
        <InspectorGroup title="Address">
          <InspectorRow label="Layout">
            <SelectField<AddressFormat>
              value={format}
              options={ADDRESS_FORMATS.map((f) => ({ value: f, label: ADDRESS_FORMAT_LABEL[f] }))}
              onChange={(addressFormat) => onChange({ ...band, addressFormat })}
            />
          </InspectorRow>
          {format === 'wrapped' && (
            <InspectorRow label="Line width">
              <NumberField
                value={band.addressWrapChars ?? DEFAULT_ADDRESS_WRAP_CHARS}
                min={12}
                max={90}
                suffix="chars"
                onChange={(addressWrapChars) => onChange({ ...band, addressWrapChars })}
              />
            </InspectorRow>
          )}
          <p className="mt-1 text-[10px] leading-snug text-slate-400">
            Customer addresses are stored as one line. This decides how it breaks:
          </p>
          <div className="mt-1 rounded bg-slate-50 px-1.5 py-1">
            {formatAddressBlock(
              ADDRESS_PREVIEW_SOURCE,
              format,
              band.addressWrapChars ?? DEFAULT_ADDRESS_WRAP_CHARS,
            ).map((line, i) => (
              <p key={i} className="text-[10px] text-slate-500">
                {line}
              </p>
            ))}
          </div>
        </InspectorGroup>
      )}
    </>
  );
}

function DocDetailsInspector({
  band,
  onChange,
}: {
  band: DocDetailsBand;
  onChange: (next: DocDetailsBand) => void;
}) {
  return (
    <>
      <InspectorGroup title="Block">
        <InspectorRow label="Heading">
          <TextField
            className="w-28"
            value={band.heading}
            placeholder="Leave blank for none"
            onChange={(heading) => onChange({ ...band, heading })}
          />
        </InspectorRow>
        <InspectorRow label="Align" hint="Right suits a right-hand column">
          <AlignPicker
            options={['left', 'right']}
            value={band.align}
            onChange={(align) => onChange({ ...band, align: align === 'left' ? 'left' : 'right' })}
          />
        </InspectorRow>
      </InspectorGroup>

      <InspectorGroup title="Rows (print order)">
        <FieldPicker
          all={META_FIELDS}
          selected={band.fields}
          labels={META_FIELD_LABEL}
          onChange={(fields) => onChange({ ...band, fields })}
        />
        <RowLabelEditor
          keys={band.fields}
          labels={band.labels}
          standard={META_FIELD_LABEL}
          onChange={(labels) => onChange({ ...band, labels })}
          note="Only the labels are editable — the values come from each document."
        />
      </InspectorGroup>
    </>
  );
}

function TotalsInspector({
  band,
  onChange,
}: {
  band: TotalsBand;
  onChange: (next: TotalsBand) => void;
}) {
  return (
    <>
      <InspectorGroup title="Layout">
        <InspectorRow label="Align">
          <AlignPicker
            options={['left', 'right']}
            value={band.align}
            onChange={(align) => onChange({ ...band, align: align === 'left' ? 'left' : 'right' })}
          />
        </InspectorRow>
        <InspectorRow label="Width">
          <NumberField
            value={band.widthPt}
            min={120}
            max={400}
            suffix="pt"
            onChange={(widthPt) => onChange({ ...band, widthPt })}
          />
        </InspectorRow>
        <InspectorRow label="VAT breakdown" hint="One line per VAT rate">
          <Checkbox
            checked={band.showVatBreakdown}
            label=""
            onChange={(showVatBreakdown) => onChange({ ...band, showVatBreakdown })}
          />
        </InspectorRow>
      </InspectorGroup>
      <InspectorGroup title="Rows (print order)">
        <FieldPicker
          all={TOTAL_ROWS}
          selected={band.rows}
          labels={TOTAL_ROW_LABEL}
          onChange={(rows) => onChange({ ...band, rows })}
        />
        <RowLabelEditor
          keys={band.rows}
          labels={band.rowLabels}
          standard={TOTAL_ROW_LABEL}
          onChange={(rowLabels) => onChange({ ...band, rowLabels })}
          note={
            band.showVatBreakdown
              ? 'The VAT label also prefixes the per-rate breakdown lines above the totals.'
              : 'Values are always the document’s own figures.'
          }
        />
      </InspectorGroup>
    </>
  );
}

function TextInspector({ band, onChange }: { band: TextBand; onChange: (next: TextBand) => void }) {
  return (
    <>
      <InspectorGroup title="Heading">
        <TextField
          className="w-full"
          value={band.heading}
          placeholder="Leave blank for no heading"
          onChange={(heading) => onChange({ ...band, heading })}
        />
      </InspectorGroup>
      <InspectorGroup title="Body">
        <BodyEditor rows={4} value={band.body} onChange={(body) => onChange({ ...band, body })} />
        <p className="mt-1 text-[10px] text-slate-400">
          The block is skipped entirely when the body resolves to nothing.
        </p>
      </InspectorGroup>
      <InspectorGroup title="Style">
        <InspectorRow label="Align">
          <AlignPicker value={band.align} onChange={(align) => onChange({ ...band, align })} />
        </InspectorRow>
        <InspectorRow label="Boxed">
          <Checkbox
            checked={band.boxed}
            label=""
            onChange={(boxed) => onChange({ ...band, boxed })}
          />
        </InspectorRow>
      </InspectorGroup>
    </>
  );
}

function BankInspector({
  band,
  bank,
  onChange,
}: {
  band: BankBand;
  bank: BankPreviewStatus;
  onChange: (next: BankBand) => void;
}) {
  return (
    <>
      {/* Only while the band is actually printing — an unticked band saying
          "no EUR account" would be noise about a block nobody wants. */}
      {band.enabled && <BankStatusNote bank={bank} />}
      <InspectorGroup title="Block">
        <InspectorRow label="Heading">
          <TextField
            className="w-32"
            value={band.heading}
            onChange={(heading) => onChange({ ...band, heading })}
          />
        </InspectorRow>
        <InspectorRow label="Boxed">
          <Checkbox
            checked={band.boxed}
            label=""
            onChange={(boxed) => onChange({ ...band, boxed })}
          />
        </InspectorRow>
      </InspectorGroup>
      <InspectorGroup title="Fields (print order)">
        <FieldPicker
          all={BANK_FIELDS}
          selected={band.fields}
          labels={BANK_FIELD_LABEL}
          onChange={(fields) => onChange({ ...band, fields })}
        />
        <p className="mt-1 text-[10px] text-slate-400">
          Values come from the organisation bank account on the document.
        </p>
      </InspectorGroup>
    </>
  );
}

function FooterInspector({
  band,
  onChange,
}: {
  band: FooterBand;
  onChange: (next: FooterBand) => void;
}) {
  return (
    <>
      <InspectorGroup title="Body">
        <BodyEditor rows={3} value={band.body} onChange={(body) => onChange({ ...band, body })} />
      </InspectorGroup>
      <InspectorGroup title="Style">
        <InspectorRow label="Align">
          <AlignPicker value={band.align} onChange={(align) => onChange({ ...band, align })} />
        </InspectorRow>
        <InspectorRow label="Page numbers">
          <Checkbox
            checked={band.showPageNumbers}
            label=""
            onChange={(showPageNumbers) => onChange({ ...band, showPageNumbers })}
          />
        </InspectorRow>
        <InspectorRow label="Rule above">
          <Checkbox
            checked={band.showRule}
            label=""
            onChange={(showRule) => onChange({ ...band, showRule })}
          />
        </InspectorRow>
      </InspectorGroup>
      <p className="text-[10px] text-slate-400">
        The footer prints at the bottom of every page.
      </p>
    </>
  );
}

export function BandInspector({
  band,
  page,
  inHeaderGrid,
  logoStatus,
  logoError,
  organisation,
  bank,
  referenceLabels,
  onChange,
}: {
  band: Band;
  /** Needed by the lines band to work out how much width is left over. */
  page: PageSettings;
  /** True when this band sits above the line items, where columns apply. */
  inHeaderGrid: boolean;
  /** Lets the header band explain an absent logo. */
  logoStatus: LogoStatus;
  logoError: string | null;
  /** The live organisation — the organisation band shows it as its defaults. */
  organisation: DocumentOrganisation;
  /** Which account the payment band resolves to, for its status note. */
  bank: BankPreviewStatus;
  /** The organisation's order-reference slot names, for the Order ref column. */
  referenceLabels: string[];
  onChange: (next: Band) => void;
}) {
  return (
    <div className="space-y-2">
      {/* The same `enabled` flag as the tick box in the band list — one state,
          reachable from both places. A separate "print this section" field would
          give one behaviour two owners that could disagree. */}
      <div className="rounded border border-slate-200 bg-slate-50 px-1.5 py-1">
        <InspectorRow
          label="Print this band"
          hint={band.enabled ? undefined : 'Kept in the layout, left off the document'}
        >
          <Checkbox
            checked={band.enabled}
            label=""
            onChange={(enabled) => onChange({ ...band, enabled })}
          />
        </InspectorRow>
      </div>

      {band.kind === 'logo' && (
        <LogoInspector
          band={band}
          logoStatus={logoStatus}
          logoError={logoError}
          onChange={onChange}
        />
      )}
      {band.kind === 'title' && <TitleInspector band={band} onChange={onChange} />}
      {band.kind === 'organisation' && (
        <OrganisationBandInspector band={band} organisation={organisation} onChange={onChange} />
      )}
      {band.kind === 'customer' && <CustomerInspector band={band} onChange={onChange} />}
      {band.kind === 'docDetails' && <DocDetailsInspector band={band} onChange={onChange} />}
      {band.kind === 'lines' && (
        <LinesBandInspector
          band={band}
          page={page}
          referenceLabels={referenceLabels}
          onChange={onChange}
        />
      )}
      {band.kind === 'totals' && <TotalsInspector band={band} onChange={onChange} />}
      {band.kind === 'text' && <TextInspector band={band} onChange={onChange} />}
      {band.kind === 'bank' && <BankInspector band={band} bank={bank} onChange={onChange} />}
      {band.kind === 'footer' && <FooterInspector band={band} onChange={onChange} />}

      {band.kind !== 'footer' && inHeaderGrid && (
        <InspectorGroup title="Position">
          <InspectorRow
            label="Column"
            hint={
              inHeaderGrid
                ? 'Which column of the header grid'
                : 'Below the line items, bands always run full width'
            }
          >
            <SelectField<string>
              value={String(band.column ?? 1)}
              options={BAND_COLUMNS.map((c) => ({ value: String(c), label: `Column ${c}` }))}
              onChange={(value) => onChange({ ...band, column: Number(value) as BandColumn })}
            />
          </InspectorRow>
        </InspectorGroup>
      )}

      {band.kind !== 'footer' && (
        <InspectorGroup title="Spacing">
          <InspectorRow label="Gap after band">
            <NumberField
              value={band.spaceAfterPt}
              min={0}
              max={80}
              suffix="pt"
              onChange={(spaceAfterPt) => onChange({ ...band, spaceAfterPt })}
            />
          </InspectorRow>
        </InspectorGroup>
      )}
    </div>
  );
}

/** Re-exported so the builder shell can offer "Add text block" with an icon. */
export const AddBandIcon = Plus;
